import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { RouterService } from '@app/routing/router.service';
import { loggerFor } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthUtilsService {
  private logger = loggerFor('AuthUtilsService');

  constructor(
    private authService: AuthenticationService,
    private routerService: RouterService
  ) {}

  redirectToLogin(url?: string): void {
    this.authService.clearAuthUser();

    if (url !== '/login') {
      this.logger.info('Redirecting to login page. Tried url: ' + url);

      // Redirect to the login page if the user isn't authenticated
      this.routerService.navigateByUrl('/login', {
        state: {
          triedUrl: url,
        },
      });
    }
  }

  /**
   * Perform URL params check based on auth-user data and update the route if there is a mismatch.
   * E.g. end-user (auth-user) cannot navigate to admin pages (need to redirect to initial route),
   * or a HTTP link points to a reseller or an enterprise or an end-user that is not in the domain of the auth-user.
   */
  postLoginUrlValidation(): void {
    const authUser = this.authService.authUser;
    if (authUser != null) {
      this.routerService.currentRouteData$.pipe(take(1)).subscribe((crd) => {
        if (this.authService.userIsEndUser()) {
          if (crd.params.userId == null) {
            this.routerService.navigateToInitialRoute();
          } else {
            const params = {
              serviceProviderId: authUser.serviceProviderName,
              enterpriseId: authUser.enterpriseName,
              userId: authUser.username,
            };
            this.navigateToURL(crd.url, crd.params, params);
            // this.routerService.navigateToRoute(crd.name, params);
          }
        } else if (this.authService.userIsEnterpriseAdmin()) {
          if (crd.params.enterpriseId == null) {
            this.routerService.navigateToInitialRoute();
          } else {
            const params = {
              serviceProviderId: authUser.serviceProviderName,
              enterpriseId: authUser.enterpriseName,
            };
            // this.routerService.navigateToRoute(crd.name, params).finally(() => {
            this.navigateToURL(crd.url, crd.params, params);
          }
        } else if (this.authService.userIsServiceProviderAdmin()) {
          if (crd.params.serviceProviderId == null) {
            if (!this.authService.userIsSystemAdmin()) {
              this.routerService.navigateToInitialRoute();
            }
          } else {
            const params = {
              serviceProviderId: authUser.serviceProviderName,
            };

            this.navigateToURL(crd.url, crd.params, params);
          }
        }
      });
    } else {
      this.logger.error('auth-user is not set');
    }
  }

  private navigateToURL(
    url: string,
    oldParams: StringMap<string>,
    newParams: StringMap<string>
  ): Promise<boolean> {
    let newUrl = url;
    Object.keys(newParams).forEach((paramName) => {
      newUrl = newUrl.replace(oldParams[paramName], newParams[paramName]);
    });
    return this.routerService.navigateByUrl(newUrl, { replaceUrl: true });
  }
}
